import type { Position } from "../types/grid.js";
import type { EnemyFaction } from "../types/entity.js";
import { chebyshev } from "../grid/distance.js";

/**
 * Camp eligibility guard (C08).
 *
 * The spec reserves camp activation for authored `camp` cells in Tiled
 * (party-and-characters.md §Camping), but even at those cells the party
 * must not be within `radius` of a hostile entity. This keeps the world-
 * freeze/long-rest sequence from being triggered while combat is
 * effectively ongoing.
 *
 * Pure; does not reach into CombatManager. Callers assemble the list of
 * nearby entities they already track and pass it in.
 */

export const DEFAULT_CAMP_HOSTILE_RADIUS = 5;

export interface CampEntity {
  readonly position: Position;
  readonly faction: EnemyFaction;
  /** Optional tag — dead entities never block camp. */
  readonly alive?: boolean;
}

export interface CampEligibilityOptions {
  /** Chebyshev-distance radius in cells. Defaults to `DEFAULT_CAMP_HOSTILE_RADIUS`. */
  readonly radius?: number;
}

export type CampIneligibleReason = "hostiles_nearby";

export interface CampEligibilityResult {
  readonly ok: boolean;
  readonly reason?: CampIneligibleReason;
  /** Instance positions (if any) that tripped the check. Useful for feed messaging. */
  readonly blockingPositions: readonly Position[];
}

/**
 * True if `entity` is alive and flagged `hostile` (neutrals and friendlies
 * never block camp).
 */
function isBlockingHostile(entity: CampEntity): boolean {
  if (entity.alive === false) return false;
  return entity.faction === "hostile";
}

/**
 * Return whether the party can camp at `partyPos` given the set of nearby
 * entities. Non-hostile and dead entities are ignored; a camp is permitted
 * when no hostile is within `radius` cells (Chebyshev).
 */
export function checkCampEligibility(
  partyPos: Position,
  entities: readonly CampEntity[],
  options: CampEligibilityOptions = {},
): CampEligibilityResult {
  const radius = options.radius ?? DEFAULT_CAMP_HOSTILE_RADIUS;
  const blockingPositions: Position[] = [];
  for (const e of entities) {
    if (!isBlockingHostile(e)) continue;
    if (chebyshev(partyPos, e.position) <= radius) {
      blockingPositions.push(e.position);
    }
  }
  if (blockingPositions.length > 0) {
    return { ok: false, reason: "hostiles_nearby", blockingPositions };
  }
  return { ok: true, blockingPositions: [] };
}
